import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { SxProps, Theme } from '@mui/material/styles';
import React, { useContext, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { AuthContext } from '_lib/featureComponents/auth/authContext';
import { CallCenterAdmin } from '_lib/featureComponents/auth/permissionHelpers';
import { appRoutePaths, getFullAppRoutePath } from '_lib/featureComponents/Nav/nav.types';
import { toSxArray } from '_lib/utils/helpers';

import { FlexBox } from '../FlexBox/FlexBox';
import { IconButton } from '../IconButton/IconButton';
import { ListItemIcon } from '../ListItemIcon/ListItemIcon';
import { ListItemText } from '../ListItemText/ListItemText';
import { Menu } from '../Menu/Menu';
import { MenuItem } from '../MenuItem/MenuItem';
import { Toolbar } from '../Toolbar/Toolbar';
import { Tooltip } from '../Tooltip/Tooltip';

import { Typography } from '../Typography/Typography';

import { AppBar, AppBarProps } from './AppBar';

type SimpleAppBarProps = {
  onBackClicked?: () => void;
  title: string;
  // TODO: APP CONTEXT?
  onAppDrawerToggleClicked?: () => void;
  position?: AppBarProps['position'];
  sx?: SxProps<Theme>;
};

export const SimpleAppBar = (props: SimpleAppBarProps) => {
  const { onBackClicked, title, onAppDrawerToggleClicked, position, sx, ...rest } = props;

  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();

  const { logout, authUserCacheResource } = useContext(AuthContext);
  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();

  return (
    <FlexBox sx={[{ minWidth: '0px' }, ...toSxArray(sx)]}>
      <AppBar position={position || 'static'} sx={{ minWidth: '0px' }} {...rest}>
        <Toolbar disableGutters>
          {onAppDrawerToggleClicked && (
            <IconButton
              color='inherit'
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: { display: 'flex' },
                [theme.breakpoints.up('md')]: { display: 'none' }
              })}
              onClick={onAppDrawerToggleClicked}>
              <MenuIcon />
            </IconButton>
          )}
          {onBackClicked && (
            <IconButton color='primaryInverted' onClick={onBackClicked} sx={{ marginLeft: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant='actionBarHeader'
            noWrap
            text={title}
            sx={{ paddingLeft: !onBackClicked ? 2 : 1, paddingRight: 4 }}
          />
          <Tooltip title='Open settings'>
            <IconButton
              size='medium'
              color='primaryInverted'
              onClick={(event: React.MouseEvent<HTMLElement>) => setUserMenuAnchorEl(event.currentTarget)}
              sx={{ marginLeft: 'auto' }}>
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchorEl}
            keepMounted
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={!!userMenuAnchorEl}
            onClose={() => setUserMenuAnchorEl(null)}>
            <MenuItem
              onClick={() => {
                setUserMenuAnchorEl(null);
                navigate(getFullAppRoutePath(appRoutePaths.Profile));
              }}>
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText>My Profile</ListItemText>
            </MenuItem>
            {cachedAuthUserQuery?.data && CallCenterAdmin({ user: cachedAuthUserQuery.data }) && (
              <MenuItem
                onClick={() => {
                  setUserMenuAnchorEl(null);
                  navigate(getFullAppRoutePath(appRoutePaths.MyCallCenter));
                }}>
                <ListItemIcon>
                  <HeadsetMicIcon />
                </ListItemIcon>
                <ListItemText>My Call Center</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setUserMenuAnchorEl(null);
                logout();
                navigate('/auth/login');
              }}>
              <ListItemIcon>
                <PowerSettingsNewIcon />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </FlexBox>
  );
};
